import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';

/** `<dialog>.showModal` is missing on old Android WebViews; fall back to the `open` attribute. */
export const supportsModalDialog = (): boolean =>
  typeof HTMLDialogElement !== 'undefined' &&
  typeof HTMLDialogElement.prototype.showModal === 'function';

/** Everything a user can tab to; used to pick the initial focus target. */
export const FOCUSABLE =
  'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';

/**
 * Page scroll lock, reference-counted: a confirm stacked on an open sheet locks
 * twice, and the styles must only be restored once the last one goes away
 * (React's unmount order between parent and child is not something to rely on).
 */
let scrollLockCount = 0;
let savedOverflow = '';
let savedOverscroll = '';

const lockPageScroll = (): (() => void) => {
  const root = document.documentElement;
  if (scrollLockCount === 0) {
    savedOverflow = root.style.overflow;
    savedOverscroll = document.body.style.overscrollBehaviorY ?? '';
    root.style.overflow = 'hidden';
    // Scoped to an open dialog, never global: a global rule would kill
    // pull-to-refresh on every page.
    document.body.style.overscrollBehaviorY = 'contain';
  }
  scrollLockCount += 1;

  let released = false;
  return () => {
    if (released) return;
    released = true;
    scrollLockCount -= 1;
    if (scrollLockCount === 0) {
      root.style.overflow = savedOverflow;
      document.body.style.overscrollBehaviorY = savedOverscroll;
    }
  };
};

export interface UseModalDialogOptions {
  /** Mounted *and* meant to be showing. The `<dialog>` may still be null. */
  isOpen: boolean;
  /**
   * A dismissal was requested: Escape (`cancel`), or a click that landed on the
   * dialog element itself (the backdrop). The caller decides what happens —
   * close straight away, or ask first.
   */
  onRequestClose: () => void;
  /**
   * The browser closed the dialog on its own (Android back gesture via
   * CloseWatcher, `form method="dialog"`). Defaults to `onRequestClose`.
   */
  onNativeClose?: () => void;
  /** Backdrop clicks dismiss. Default `true`. */
  closeOnBackdropClick?: boolean;
  /**
   * Element to focus once open. Defaults to the first focusable descendant.
   * Return `null` to fall back to that default.
   */
  getInitialFocus?: (dialog: HTMLDialogElement) => HTMLElement | null;
  /**
   * Where focus goes when the dialog closes. Defaults to whatever had focus
   * when it opened — pass a ref when the trigger never actually took focus
   * (a pointer click on some platforms leaves `document.activeElement` on the
   * body).
   */
  returnFocusTo?: React.RefObject<HTMLElement | null>;
}

export interface UseModalDialogResult {
  /** The dialog node, as state: consumers re-render once it exists. */
  dialogEl: HTMLDialogElement | null;
  /** `ref` for the `<dialog>`. */
  setDialogRef: (node: HTMLDialogElement | null) => void;
  /** False on old WebViews, where the `open` + `position: fixed` path is used. */
  isNativeModal: boolean;
  /** Spread onto the `<dialog>`: open fallback, cancel/backdrop/close wiring. */
  dialogProps: {
    open: true | undefined;
    onCancel: (event: React.SyntheticEvent<HTMLDialogElement>) => void;
    onClose: () => void;
    onClick: (event: React.MouseEvent<HTMLDialogElement>) => void;
    onKeyDown: (event: React.KeyboardEvent<HTMLDialogElement>) => void;
  };
}

/**
 * One `<dialog>`-as-modal: `showModal()` (with the `open` fallback), Escape and
 * backdrop dismissal, page scroll/overscroll lock, focus in on open and back to
 * the trigger on close.
 *
 * Shared by the app shell's phone drawer, `SideBarModal` and `ConfirmDialog` so
 * the top-layer plumbing lives in exactly one place. Every later `showModal()`
 * stacks above the earlier ones, which is what lets a confirm sit on top of an
 * already-open sheet.
 */
export function useModalDialog({
  isOpen,
  onRequestClose,
  onNativeClose,
  closeOnBackdropClick = true,
  getInitialFocus,
  returnFocusTo,
}: UseModalDialogOptions): UseModalDialogResult {
  // State, not a ref: consumers of the node (portal targets) must re-render
  // once it exists, and the open/focus effect must run after it mounts.
  const [dialogEl, setDialogEl] = useState<HTMLDialogElement | null>(null);
  const previousFocusRef = useRef<Element | null>(null);
  const isNativeModal = supportsModalDialog();

  // Handlers change identity every render; the effect must not re-run for that.
  const optionsRef = useRef({
    onRequestClose,
    onNativeClose,
    closeOnBackdropClick,
    getInitialFocus,
    returnFocusTo,
  });
  optionsRef.current = {
    onRequestClose,
    onNativeClose,
    closeOnBackdropClick,
    getInitialFocus,
    returnFocusTo,
  };

  useLayoutEffect(() => {
    if (!isOpen || !dialogEl) return undefined;

    previousFocusRef.current = document.activeElement;
    if (isNativeModal && !dialogEl.open) {
      dialogEl.showModal();
    }

    const { getInitialFocus: pickFocus } = optionsRef.current;
    const target =
      pickFocus?.(dialogEl) ?? dialogEl.querySelector<HTMLElement>(FOCUSABLE);
    target?.focus();

    const unlockScroll = lockPageScroll();

    return () => {
      unlockScroll();
      const previous =
        optionsRef.current.returnFocusTo?.current ?? previousFocusRef.current;
      if (previous instanceof HTMLElement && previous.isConnected) {
        previous.focus();
      }
    };
  }, [isOpen, dialogEl, isNativeModal]);

  const handleCancel = useCallback(
    (event: React.SyntheticEvent<HTMLDialogElement>) => {
      // Escape. Keep the element open and run our own close path instead.
      event.preventDefault();
      optionsRef.current.onRequestClose();
    },
    []
  );

  const handleNativeClose = useCallback(() => {
    const { onNativeClose: onNative, onRequestClose: onRequest } =
      optionsRef.current;
    (onNative ?? onRequest)();
  }, []);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLDialogElement>) => {
      // Clicks on the dialog element itself land on the ::backdrop (or the
      // empty area around the content); clicks on children have another target.
      if (
        !optionsRef.current.closeOnBackdropClick ||
        event.target !== event.currentTarget
      ) {
        return;
      }
      optionsRef.current.onRequestClose();
    },
    []
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDialogElement>) => {
      // Native modals raise `cancel` for Escape; the `open` fallback does not.
      if (isNativeModal || event.key !== 'Escape') return;
      event.preventDefault();
      optionsRef.current.onRequestClose();
    },
    [isNativeModal]
  );

  return {
    dialogEl,
    setDialogRef: setDialogEl,
    isNativeModal,
    dialogProps: {
      // Fallback path only: without `showModal` the browser does not place the
      // element in the top layer, so `open` + fixed it is.
      open: isNativeModal ? undefined : true,
      onCancel: handleCancel,
      onClose: handleNativeClose,
      onClick: handleClick,
      onKeyDown: handleKeyDown,
    },
  };
}

export default useModalDialog;
